import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useController } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { SingleValue } from "react-select";
import { useDebouncedCallback } from "use-debounce";
import { FormContext } from "../../App";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Label from "../Label/Label";
import style from "./LocationInput.module.css";

const PLACES_BASE_URL =
  "https://8q2z5k9ah4.execute-api.eu-central-1.amazonaws.com/default/shapr3d-ashby-proxy/gcp-places-api";

interface LocationOption {
  label: string;
  value: string;
  placeId: string;
}

interface LocationValue {
  country?: string;
  city?: string;
  region?: string;
}

interface LocationInputProps {
  label: string;
  name: string;
  required?: boolean;
}

type LocationFieldValues = Record<string, LocationValue | null>;

interface AutocompleteResponse {
  suggestions?: Array<{
    placePrediction?: {
      placeId: string;
      text?: {
        text: string;
      };
    };
  }>;
}

interface AddressComponent {
  longText?: string;
  types?: string[];
}

interface PlaceDetailsResponse {
  addressComponents?: AddressComponent[];
}

const fetchLocationSuggestions = async (
  query: string,
  sessionToken: string,
  signal: AbortSignal,
): Promise<LocationOption[]> => {
  if (!query.trim()) {
    return [];
  }

  const response = await fetch(`${PLACES_BASE_URL}/places:autocomplete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": "en",
      "X-Goog-FieldMask":
        "suggestions.placePrediction.placeId,suggestions.placePrediction.text",
    },
    signal,
    body: JSON.stringify({
      input: query.trim(),
      sessionToken,
      languageCode: "en",
      includedPrimaryTypes: ["(cities)"],
      includeQueryPredictions: false,
    }),
  });

  if (!response.ok) {
    throw new Error("Autocomplete failed");
  }

  const data = (await response.json()) as AutocompleteResponse;

  return (data.suggestions ?? [])
    .map((suggestion) => suggestion.placePrediction)
    .filter((prediction): prediction is NonNullable<typeof prediction> =>
      Boolean(prediction?.placeId),
    )
    .map((prediction) => {
      const description = prediction.text?.text ?? "";
      return {
        label: description,
        value: description,
        placeId: prediction.placeId,
      };
    })
    .filter((option) => option.label);
};

const fetchPlaceLocationDetails = async (
  placeId: string,
  sessionToken: string,
  signal: AbortSignal,
): Promise<LocationValue | null> => {
  const params = new URLSearchParams({
    fields: "addressComponents",
    languageCode: "en",
    sessionToken,
  });

  const response = await fetch(
    `${PLACES_BASE_URL}/places/${encodeURIComponent(placeId)}?${params}`,
    {
      method: "GET",
      headers: {
        "Accept-Language": "en",
        "X-Goog-FieldMask": "addressComponents",
      },
      signal,
    },
  );

  if (!response.ok) {
    throw new Error("Place details failed");
  }

  const data = (await response.json()) as PlaceDetailsResponse;
  const components = data.addressComponents ?? [];
  const getComponent = (type: string) =>
    components.find((component) => component.types?.includes(type));
  const cityComponent =
    getComponent("locality") ??
    getComponent("postal_town") ??
    getComponent("administrative_area_level_2");
  const regionComponent = getComponent("administrative_area_level_1");
  const countryComponent = getComponent("country");

  const city = cityComponent?.longText;
  const region = regionComponent?.longText;
  const country = countryComponent?.longText;

  if (!city && !region && !country) {
    return null;
  }

  return {
    city,
    region,
    country,
  };
};

const LocationInput: React.FC<LocationInputProps> = ({
  label,
  name,
  required,
}) => {
  const { control, errors } = React.useContext(FormContext);
  const {
    field: { onChange, onBlur, value, ref },
  } = useController<LocationFieldValues>({
    control,
    name,
    rules: {
      required: required && "Required field",
    },
  });

  const [apiError, setApiError] = useState<string | null>(null);
  const [selectedOption, setSelectedOption] = useState<LocationOption | null>(
    null,
  );
  const sessionTokenRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const detailsAbortControllerRef = useRef<AbortController | null>(null);
  const pendingPlaceIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      detailsAbortControllerRef.current?.abort();
      abortControllerRef.current = null;
      detailsAbortControllerRef.current = null;
      pendingPlaceIdRef.current = null;
    };
  }, []);

  const getSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = crypto.randomUUID();
    }
    return sessionTokenRef.current;
  };

  const resetSessionToken = () => {
    sessionTokenRef.current = null;
  };

  const abortAutocompleteRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const abortDetailsRequest = () => {
    if (detailsAbortControllerRef.current) {
      detailsAbortControllerRef.current.abort();
      detailsAbortControllerRef.current = null;
    }
  };

  const clearPendingPlace = () => {
    pendingPlaceIdRef.current = null;
  };

  const clearSelection = () => {
    abortDetailsRequest();
    clearPendingPlace();
    onChange(null);
    resetSessionToken();
  };

  const debouncedLoadOptions = useDebouncedCallback(
    (query: string, resolve: (options: LocationOption[]) => void) => {
      abortAutocompleteRequest();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      fetchLocationSuggestions(query, getSessionToken(), controller.signal)
        .then((options) => {
          if (controller.signal.aborted) {
            return;
          }
          setApiError(null);
          resolve(options);
        })
        .catch(() => {
          if (controller.signal.aborted) {
            return;
          }
          setApiError("Unable to load locations.");
          resolve([]);
        });
    },
    300,
  );

  const loadOptions = (query: string): Promise<LocationOption[]> => {
    if (!query.trim()) {
      abortAutocompleteRequest();
      setApiError(null);
      return Promise.resolve([]);
    }

    return new Promise((resolve) => {
      debouncedLoadOptions(query, resolve);
    });
  };

  const showApiError = !errors[name] ? apiError : null;

  const formatLocationLabel = (location: LocationValue) =>
    [location.city, location.region, location.country]
      .filter(Boolean)
      .join(", ");

  const resolveDisplayLabel = (
    currentValue: LocationValue | null | undefined,
  ) => {
    if (!currentValue) {
      return "";
    }

    return formatLocationLabel(currentValue);
  };

  const displayLabel = resolveDisplayLabel(value);

  const handleSelectChange = (option: SingleValue<LocationOption>) => {
    if (!option) {
      clearSelection();
      setSelectedOption(null);
      return;
    }

    const sessionToken = getSessionToken();
    setSelectedOption(option);
    onChange(null);
    resetSessionToken();

    abortDetailsRequest();

    pendingPlaceIdRef.current = option.placeId;
    const controller = new AbortController();
    detailsAbortControllerRef.current = controller;

    fetchPlaceLocationDetails(option.placeId, sessionToken, controller.signal)
      .then((location) => {
        if (
          controller.signal.aborted ||
          pendingPlaceIdRef.current !== option.placeId
        ) {
          return;
        }

        if (location) {
          onChange(location);
        }
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return;
        }
        setSelectedOption(null);
        setApiError("Unable to load location details.");
      });
  };

  const handleInputChange = (nextValue: string, action: string) => {
    if (action === "input-change") {
      abortDetailsRequest();
      clearPendingPlace();
      setSelectedOption(null);
      if (!nextValue.trim()) {
        if (value) {
          onChange(null);
        }
        resetSessionToken();
        setApiError(null);
      }
    }
    return nextValue;
  };

  return (
    <div className={style.inputContainer}>
      <Label text={label} required={required} />
      <AsyncSelect<LocationOption, false>
        inputId={`${name}-location`}
        className={classNames({ [style.error]: errors[name] })}
        classNamePrefix="react-select"
        loadOptions={loadOptions}
        isSearchable
        cacheOptions
        isClearable
        placeholder={"Please select"}
        loadingMessage={() => "Loading..."}
        noOptionsMessage={() => "No matches"}
        value={selectedOption}
        onInputChange={(nextValue, actionMeta) =>
          handleInputChange(nextValue, actionMeta.action)
        }
        onChange={handleSelectChange}
        ref={ref}
        onBlur={onBlur}
      />
      <input
        type="hidden"
        name={name}
        value={displayLabel}
        onChange={() => {}}
      />
      <ErrorMessage name={name} errorText={showApiError ?? undefined} />
    </div>
  );
};

export default LocationInput;
